<script setup lang="ts">
import { useEventListener, useRafFn } from '@vueuse/core'
import { ref, watch } from 'vue'
import { spotlightState } from '@/shared/composables/useSpotlight'

// Gap between a ring and the element it surrounds, in px
const RING_OFFSET = 4

const { elements, clear } = spotlightState

const rings = ref<{ top: number; left: number; width: number; height: number }[]>([])

// Follows the elements every frame while lit, so a ring keeps up with scrolling,
// smooth scrollIntoView and layout shifts
const measure = () => {
  rings.value = elements.value
    .filter((element) => element.isConnected)
    .map((element) => {
      const rect = element.getBoundingClientRect()
      return {
        top: rect.top - RING_OFFSET,
        left: rect.left - RING_OFFSET,
        width: rect.width + RING_OFFSET * 2,
        height: rect.height + RING_OFFSET * 2
      }
    })
}

const { pause, resume } = useRafFn(measure, { immediate: false })

watch(elements, (lit) => {
  if (lit.length) {
    measure()
    resume()
  } else {
    pause()
    rings.value = []
  }
})

// Any click or key press means the user has moved on
useEventListener(window, 'pointerdown', clear, { capture: true, passive: true })
useEventListener(window, 'keydown', clear, { capture: true, passive: true })
</script>

<template>
  <div
    v-for="(ring, index) in rings"
    :key="index"
    class="border-primary ring-primary/25 pointer-events-none fixed z-[1000] animate-pulse rounded-md border-2 ring-4"
    :style="{ top: `${ring.top}px`, left: `${ring.left}px`, width: `${ring.width}px`, height: `${ring.height}px` }"
  />
</template>
